<template>
    <div class="d-flex items-center justify-content-center w-full">
        <div class="width-500"
             v-if="!isSubmitted && !isSubmitSuccess">
            <div class="text-center">
                <h3 class="signup--title">{{ currentStep === 1 ? 'Welcome to Aloware' : 'Tell us about your company' }}</h3>
                <p class="signup--subtitle" v-if="currentStep === 1">Start your trial, <strong>no credit card required.</strong></p>
                <!-- Progress Bar -->
                <div class="signup-progress">
                    <div class="signup-progress__bar">
                        <div class="signup-progress__fill" :style="{ width: progressPercentage + '%' }"></div>
                    </div>
                    <div class="signup-progress__text">
                        Step {{ currentStep }} of 2
                    </div>
                </div>
            </div>
            <div class="signup-wrapper">
                <div class="signup-header">
                    <el-button
                        class="text-dark signup-back-button"
                        type="text"
                        icon="el-icon-arrow-left"
                        @click="goToStep1"
                        v-if="currentStep === 2"
                        :disabled="isLoading">
                    </el-button>
                    <div class="login-brand">
                        <img :src="statics.logo"
                             alt="."/>
                    </div>
                </div>

                <el-form class="mt-3"
                         ref="signupForm"
                         label-position="top"
                         label-width="300px"
                         :model="ssu"
                         :rules="rules">

                    <!-- Step 1 Fields -->
                    <div v-if="currentStep === 1">
                        <el-form-item prop="email">
                            <el-input placeholder="Work email"
                                      type="email"
                                      :loading="isValidatingEmail"
                                      v-model="ssu.email">
                                <template slot="append"
                                          v-if="isValidatingEmail">
                                    <i class="material-icons loader pull-right">
                                        &#xE863;
                                    </i>
                                </template>
                            </el-input>
                        </el-form-item>
                        <!-- two columns first_name and last_name -->
                        <el-row class="p-0">
                            <el-col class="p-0 pr-3"
                                    :span="12">
                                <el-form-item prop="first_name">
                                    <el-input placeholder="First name"
                                              v-model="ssu.first_name">
                                    </el-input>
                                </el-form-item>
                            </el-col>
                            <el-col class="p-0"
                                    :span="12">
                                <el-form-item prop="last_name">
                                    <el-input placeholder="Last name"
                                              v-model="ssu.last_name">
                                    </el-input>
                                </el-form-item>
                            </el-col>
                        </el-row>

                        <el-form-item prop="company_name">
                            <el-input class=""
                                      placeholder="Company name"
                                      v-model="ssu.company_name">
                            </el-input>
                        </el-form-item>

                        <phone :has-error="hasPhoneNumberError"
                               :value="currentPhoneData"
                               @input="updatePhoneNumber"></phone>
                        <el-form-item prop="phone_number">
                            <span class="el-form-item__error"
                                  v-show="hasPhoneNumberError">
                                {{ validatePhoneNumber() }}
                            </span>
                        </el-form-item>

                        <!-- reCAPTCHA -->
                        <vue-recaptcha ref="recaptcha"
                                       class="g-recaptcha pt-3"
                                       :sitekey="site_key"
                                       v-if="showRecaptcha"
                                       @verify="onVerify"
                                       @expired="onExpired">
                        </vue-recaptcha>

                        <!-- Error display for step 1 -->
                        <div v-if="submissionError && currentStep === 1" class="alert alert-danger mt-3">
                            {{ submissionError }}
                        </div>

                        <!-- Terms and Conditions Text -->
                        <div class="mt-3 mb-0">
                            <p class="terms-text__content">
                                By clicking "Next", you agree to Aloware's
                                <a class="terms-text__link"
                                   href="https://aloware.com/terms-and-conditions"
                                   target="_blank">
                                    Terms and Conditions
                                </a>
                                and
                                <a class="terms-text__link"
                                   href="https://aloware.com/privacy-policy"
                                   target="_blank">
                                    Privacy Policy
                                </a>.
                            </p>
                        </div>
                    </div>

                    <!-- Step 2 Fields -->
                    <div v-if="currentStep === 2">
                        <el-form-item prop="company_size">
                            <el-select v-model="ssu.company_size"
                                       class="no-select w-100"
                                       placeholder="Company Size"
                                       clearable>
                                <el-option
                                    v-for="size in COMPANY_SIZE_OPTIONS"
                                    :key="size"
                                    :label="size + ' Employees'"
                                    :value="size"/>
                            </el-select>
                        </el-form-item>

                        <el-form-item prop="team_size">
                            <el-select v-model="ssu.team_size"
                                       class="no-select w-100"
                                       placeholder="Number of Licenses"
                                       clearable>
                                <el-option
                                    v-for="size in TEAM_SIZE_OPTIONS"
                                    :key="size"
                                    :label="size + ' Users'"
                                    :value="size"/>
                            </el-select>
                        </el-form-item>

                        <el-form-item prop="company_crm">
                            <el-select v-model="ssu.company_crm"
                                       class="no-select w-100"
                                       placeholder="Company CRM"
                                       multiple
                                       clearable
                                       @change="handleCrmChange">
                                <el-option
                                    v-for="crm in COMPANY_CRM_OPTIONS"
                                    :key="crm"
                                    :label="getCrmDisplayLabel(crm)"
                                    :value="crm"/>
                            </el-select>
                        </el-form-item>

                        <el-form-item prop="teams">
                            <el-select v-model="ssu.teams"
                                       class="no-select w-100"
                                       placeholder="What teams will use Aloware?"
                                       multiple
                                       clearable
                                       @change="handleTeamsChange">
                                <el-option
                                    v-for="team in TEAMS_OPTIONS"
                                    :key="team"
                                    :label="getTeamDisplayLabel(team)"
                                    :value="team"/>
                            </el-select>
                        </el-form-item>

                        <!-- Checkboxes and reCAPTCHA -->

                        <el-checkbox class="mt-3 signup-checkbox__container"
                                     v-model="ssu.communications_opt_in">
                            By checking this box, I agree to receive communications, including email, calls, and text messages from Aloware regarding announcements and company updates.
                            Reply to any messages with STOP at any time to stop receiving messages and request for help by replying HELP.
                            The frequency of messages varies. Message and data rates may apply.
                        </el-checkbox>
                    </div>

                    <!-- Navigation Buttons -->
                    <div class="mt-3">
                        <el-button v-if="currentStep === 1"
                                   class="btn-block signup-submit__button"
                                   type="primary"
                                   size="large"
                                   :disabled="isStep1ButtonDisabled"
                                   @click="goToStep2">
                            Next
                            <i class="el-icon-arrow-right el-icon-right pull-right"/>
                        </el-button>

                        <div v-if="currentStep === 2">
                            <el-button class="btn-block signup-submit__button"
                                       type="primary"
                                       size="large"
                                       :disabled="isStartTrialButtonDisabled"
                                       @click="onSubmit">
                                <i class="material-icons loader pull-right"
                                   v-show="isLoading">
                                    &#xE863;
                                </i>
                                Start Trial
                                <i class="el-icon-arrow-right el-icon-right pull-right"
                                   v-show="!isLoading"/>
                            </el-button>
                        </div>
                    </div>
                </el-form>
            </div>
        </div>

        <!-- SSU rejected validation message -->
        <div class="signup-wrapper__success"
             v-else-if="isSubmitted && !isSubmitSuccess && !submissionError && isSSURejected">
            <div>
                <h3 class="signup-wrapper__success__title">
                    Sorry, we couldn’t verify your business information.
                </h3>
                <hr/>
                <p>
                    Please call us at: (855) 256-2001 <br/>
                    or email us at: <a href="mailto:sales@aloware.com">sales@aloware.com</a>
                </p>
            </div>
        </div>

        <!-- All validation errors with login redirect -->
        <div class="signup-wrapper__success" v-else-if="isSubmitted && !isSubmitSuccess && submissionError && !isSSURejected">
            <div>
                <h3 class="signup-wrapper__success__title">{{ submissionError }}</h3>
                <p class="signup-wrapper__success__subtitle">
                    <a href="/login">
                        Click here to proceed to the login page
                    </a>
                </p>
            </div>
        </div>

        <div class="signup-wrapper__success" v-else>
            <div>
                <h3 class="signup-wrapper__success__title">Your trial account is almost ready!</h3>

                <div class="mt-4">
                    <h4>What's Next?</h4>
                    <p>Please check your inbox and confirm your email.</p>
                    <a href="https://meetings.hubspot.com/alwr/aloware-demo"
                       class="btn btn-primary"
                       target="_blank">
                        <i class='material-icons'>perm_contact_calendar</i>
                        Book a Demo
                    </a>
                </div>
            </div>
        </div>

        <static-assets-preloader :assets="statics"/>
    </div>
</template>

<script>
import {VueRecaptcha} from "vue-recaptcha"
import Phone from "./utilities/phone"
import moment from "moment-timezone"
import { PRE_SIGNUP_SUBMISSION_TYPES } from "../../admin/constants/pre-signup"
import { COMPANY_CRM_OPTIONS, COMPANY_CRM_DISPLAY_NAMES, TEAMS_OPTIONS, COMPANY_SIZE_OPTIONS, TEAM_SIZE_OPTIONS } from "../constants/signup-options"

export default {
    name: 'signup',

    components: {
        'vue-recaptcha': VueRecaptcha,
        Phone,
    },

    props: {
        statics: {required: true}
    },

    data() {
        return {
            site_key: localStorage.getItem('recaptcha_site_key'),
            isLoading: false,
            isValidatingEmail: false,
            error: null,
            currentStep: 1,
            COMPANY_CRM_OPTIONS,
            TEAMS_OPTIONS,
            COMPANY_SIZE_OPTIONS,
            TEAM_SIZE_OPTIONS,
            ssu: {
                email: null,
                first_name: null,
                last_name: null,
                company_name: null,
                phone_number: null,
                country_code: '1',
                country: 'US',
                communications_opt_in: false,
                recaptcha_response: null,
                team_size: null,
                company_size: null,
                company_crm: [],
                teams: [],
                referrer: null,
            },
            rules: {
                email: [
                    {required: true, message: 'Please input the email address', trigger: 'blur'},
                    {type: 'email', message: 'Please input a valid email address', trigger: ['blur', 'change']},
                    {required: true, validator: this.validateEmailWithServer, message: 'You need to enter a valid work email address. Please use another one.', trigger: 'blur'}
                ],
                first_name: [
                    {required: true, message: 'Please input the first name', trigger: 'blur'}
                ],
                last_name: [
                    {required: true, message: 'Please input the last name', trigger: 'blur'}
                ],
                company_name: [
                    {required: true, message: 'Please input the company name', trigger: 'blur'}
                ],
                team_size: [
                    {required: true, message: 'Please select the number of licenses', trigger: 'change'}
                ],
                company_size: [
                    {required: true, message: 'Please select the company size', trigger: 'change'}
                ],
                company_crm: [
                    {required: true, message: 'Please select your company CRM', trigger: 'change'}
                ],
                teams: [
                    {required: true, message: 'Please select which teams will use Aloware', trigger: 'change'}
                ],
            },
            userIpAddress: '',
            isEmailValidated: false,
            isSubmitted: false,
            isSubmitSuccess: false,
            isHubspotSubmissionSuccess: false,
            submissionError: null,
            isSSURejected: false,
            lastSubmittedStep1Data: null,
            trackingParams: {},
        }
    },

    computed: {
        showRecaptcha() {
            return !!this.site_key
        },

        isStep1ButtonDisabled() {
            return this.isLoading || !this.validateStep1FieldsFilled()
        },

        isStartTrialButtonDisabled() {
            return this.isLoading || !this.validateAllFieldsFilled()
        },

        hasPhoneNumberError() {
            return this.ssu.phone_number && this.validatePhoneNumber() !== true
        },

        progressPercentage() {
            return this.currentStep === 1 ? 0 : 50
        },

        currentPhoneData() {
            if (this.ssu.phone_number && this.ssu.country_code && this.ssu.country) {
                return {
                    phoneNumber: this.ssu.phone_number,
                    countryData: {
                        dialCode: this.ssu.country_code.replace('+', ''),
                        iso2: this.ssu.country.toLowerCase()
                    }
                }
            }
            return null
        },

        hubspotFormURL() {
            return (!localStorage || typeof localStorage.getItem !== 'function' || localStorage.getItem('app_env') === 'production')
                ? "https://api.hsforms.com/submissions/v3/integration/submit/4592972/9d391000-0cea-41fd-81fe-d795aeadbfb7"
                : "https://api.hsforms.com/submissions/v3/integration/submit/8446098/be426fa2-e7db-4712-8b90-b1e4b94c9dcc"
        },

        hubspotStep1FormURL() {
            return (!localStorage || typeof localStorage.getItem !== 'function' || localStorage.getItem('app_env') === 'production')
                ? "https://api.hsforms.com/submissions/v3/integration/submit/4592972/2bdf3341-a30d-423c-824e-9a8bf4ca2ee0"
                : "https://api.hsforms.com/submissions/v3/integration/submit/8446098/9c6a340e-c881-4b56-88ce-bd6d962b4a44"
        },
    },

    methods: {
        clearError() {
            this.verification_message_type = 'success'
            this.verification_message = ''
        },

        getCrmDisplayLabel(crm) {
            return COMPANY_CRM_DISPLAY_NAMES[crm] || crm
        },

        getTeamDisplayLabel(team) {
            if (team === 'Select All') {
                return team
            }
            const teamLabels = {
                'sales': 'Sales',
                'support': 'Support',
                'marketing': 'Marketing',
                'customer_success': 'Customer Success',
                'other': 'Other'
            }
            return teamLabels[team] || team
        },

        handleCrmChange(selectedCrms) {
            // If "none" is being added to existing selections, clear all others
            if (selectedCrms.includes('none') && this.ssu.company_crm.length > 0 && !this.ssu.company_crm.includes('none')) {
                this.ssu.company_crm = ['none']
            }
            // If any other option is selected and "none" was previously selected, remove "none"
            else if (selectedCrms.some(crm => crm !== 'none') && this.ssu.company_crm.includes('none')) {
                this.ssu.company_crm = selectedCrms.filter(crm => crm !== 'none')
            }
            // Otherwise, keep the current selection
            else {
                this.ssu.company_crm = selectedCrms
            }
        },

        formatCrmForHubspot(crmArray) {
            // For HubSpot multi-checkbox, send as semicolon-separated string
            if (!crmArray || crmArray.length === 0) {
                return ''
            }
            return crmArray.join(';')
        },

        formatTeamsForHubspot(teamsArray) {
            // For HubSpot multi-checkbox, send as semicolon-separated string
            if (!teamsArray || teamsArray.length === 0) {
                return ''
            }
            // Filter out 'Select All' if it exists
            const filteredTeams = teamsArray.filter(team => team !== 'Select All')
            return filteredTeams.join(';')
        },

        onVerify(response) {
            this.ssu.recaptcha_response = response
        },

        onExpired() {
            this.ssu.recaptcha_response = null
        },

        getCleanedPhoneNumber(phone) {
            if (!phone) {
                return ''
            }

            return phone.replace(/[^\d]/g, '')
        },

        updatePhoneNumber(phoneData) {
            this.ssu.phone_number = phoneData?.phoneNumber
            this.ssu.country_code = `+${phoneData?.countryData?.dialCode}`
            this.ssu.country = phoneData?.countryData?.iso2.toUpperCase()
        },

        validateEmail(email) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            return emailRegex.test(email) || 'Invalid email address'
        },

        async validateEmailWithServer(rule, value, callback) {
            const email = value || this.ssu.email
            this.isValidatingEmail = true

            try {
                const response = await axios.get('/api/v1/risk-assessment/email-validation', {params: {email}})

                if (response.status === 200 && response.data?.email_risky !== undefined) {
                    if (response.data.email_risky) {
                        this.isEmailValidated = false
                        return callback(new Error('You need to enter a valid work email address. Please use another one.'))
                    }

                    this.isEmailValidated = true
                    return callback()
                }

                this.isEmailValidated = false
                return callback(new Error('Failed to validate email'))
            } catch (error) {
                console.error('Error validating email:', error)
                this.isEmailValidated = false
                return callback(new Error('Failed to validate email'))
            } finally {
                this.isValidatingEmail = false
            }
        },


        validatePhoneNumber() {
            const formattedPhone = this.$options.filters.fixPhone(this.ssu.phone_number, 'E164', true)

            // if the phone number is empty or invalid
            if (!formattedPhone || formattedPhone === '-') {
                return 'Please enter a valid phone number'
            }

            return true
        },

        validateStep1FieldsFilled() {
            return (
                this.ssu.email &&
                this.validateEmail(this.ssu.email) === true &&
                this.isEmailValidated &&
                this.ssu.first_name &&
                this.ssu.last_name &&
                this.ssu.company_name &&
                this.validatePhoneNumber() === true &&
                (this.ssu.recaptcha_response || !this.site_key)
            )
        },

        validateAllFieldsFilled() {
            return (
                this.ssu.email &&
                this.validateEmail(this.ssu.email) === true &&
                this.ssu.first_name &&
                this.ssu.last_name &&
                this.ssu.company_name &&
                this.ssu.team_size &&
                this.ssu.company_size &&
                this.ssu.company_crm &&
                this.ssu.company_crm.length > 0 &&
                this.ssu.teams &&
                this.ssu.teams.length > 0 &&
                this.validatePhoneNumber() === true
            )
        },

        checkIfFormFieldsAreValid() {
            if (!this.$refs.signupForm) {
                return false
            }

            let isValid

            this.$refs.signupForm.validate(async (valid) => {
                if (!valid) {
                    return isValid = false
                }

                return isValid = true
            })

            if (!isValid) {
                return false
            }

            return true
        },

        getHubspotCookie() {
            const name = 'hubspotutk='
            const decodedCookie = decodeURIComponent(document.cookie)
            const ca = decodedCookie.split(';')

            for (let i = 0; i < ca.length; i++) {
                let c = ca[i]

                while (c.charAt(0) === ' ') {
                    c = c.substring(1)
                }

                if (c.indexOf(name) === 0) {
                    return c.substring(name.length, c.length)
                }
            }

            return ''
        },

        getCurrentDateTimeInPST() {
            return moment().tz('America/Los_Angeles').format();
        },

        async submitToHubspotStep1() {
            const hutk = this.getHubspotCookie() || '';

            const hubspotData = {
                fields: [
                    {name: 'email', value: this.ssu.email},
                    {name: 'firstname', value: this.ssu.first_name},
                    {name: 'lastname', value: this.ssu.last_name},
                    {name: 'company', value: this.ssu.company_name},
                    {name: 'phone', value: this.ssu.phone_number},
                    {name: 'ip_address', value: this.userIpAddress},
                    {name: 'accepted_terms_and_conditions', value: this.getCurrentDateTimeInPST()},
                    {name: 'accepted_privacy_policy', value: this.getCurrentDateTimeInPST()},
                    ...Object.entries(this.trackingParams).map(([name, value]) => ({name, value})),
                ],
                context: {
                    hutk,
                    ipAddress: this.userIpAddress,
                    pageUri: window.location.href,
                    pageName: document.title
                }
            }

            try {
                const response = await axios.post(this.hubspotStep1FormURL, hubspotData)

                if (response.status === 500) {
                    this.isHubspotSubmissionSuccess = false
                    return
                }

                const data = response.data

                if (data.errors && data.errors.length > 0) {
                    this.isHubspotSubmissionSuccess = false
                    return
                }

                this.isHubspotSubmissionSuccess = true
                return data
            } catch (error) {
                console.error('Failed to submit to HubSpot Step 1', error)
                return false
            }
        },

        async submitToHubspot() {
            const hutk = this.getHubspotCookie() || '';

            const hubspotData = {
                fields: [
                    {name: 'email', value: this.ssu.email},
                    {name: 'firstname', value: this.ssu.first_name},
                    {name: 'lastname', value: this.ssu.last_name},
                    {name: 'company', value: this.ssu.company_name},
                    {name: 'phone', value: this.ssu.phone_number},
                    {name: 'pre_sign_up_license_count', value: this.ssu.team_size},
                    {name: 'pre_sign_up_company_size', value: this.ssu.company_size},
                    {name: 'pre_sign_up_company_crm', value: this.ssu.company_crm ? this.ssu.company_crm.join(';') : ''},
                    {name: 'pre_sign_up_teams', value: this.ssu.teams ? this.ssu.teams.filter(team => team !== 'Select All').join(';') : ''},
                    {name: 'communications_opt_in', value: this.ssu.communications_opt_in ? this.getCurrentDateTimeInPST() : ''},
                    ...Object.entries(this.trackingParams).map(([name, value]) => ({name, value})),
                ],
                context: {
                    hutk,
                    ipAddress: this.userIpAddress,
                    pageUri: window.location.href,
                    pageName: document.title
                }
            }

            try {
                const response = await axios.post(this.hubspotFormURL, hubspotData)

                if (response.status === 500) {
                    this.isHubspotSubmissionSuccess = false
                    return
                }

                const data = response.data

                if (data.errors && data.errors.length > 0) {
                    this.isHubspotSubmissionSuccess = false
                    return
                }

                this.isHubspotSubmissionSuccess = true
                return data
            } catch (error) {
                console.error('Failed to submit to HubSpot', error)
                // Reload the page
                return false
            }
        },

        async submitToRiskAssessment(payload) {
            try {
                const response = await axios.post('/api/v1/risk-assessment/pre-signup', payload)

                if (response.status === 500) {
                    this.isSubmitSuccess = false
                    return false
                }

                const data = response.data

                if (data.errors) {
                    // Case 1: ['validation', 'message'] array format from RiskAssessmentController
                    if (Array.isArray(data.errors) && data.errors[0] === 'validation') {
                        this.isSubmitSuccess = false
                        this.isSSURejected = true
                        return data
                    }

                    // Case 2: {validation: ['message']} object format from validation
                    if (data.errors.validation) {
                        this.isSubmitSuccess = false
                        this.submissionError = data.errors.validation[0]
                        return data
                    }

                    // For other types of errors
                    this.submissionError = Array.isArray(data.errors)
                        ? data.errors[1]
                        : Object.values(data.errors)[0][0]
                    return false
                }

                this.isSubmitSuccess = true
                return data
            } catch (error) {
                console.error('Failed to submit to Risk Assessment', error)

                if (error.response?.status === 422) {
                    const errors = error.response.data.errors
                    // Handle all validation errors with submissionError
                    if (errors.validation) {
                        this.isSubmitSuccess = false
                        this.submissionError = errors.validation[0]
                        return error.response.data
                    }
                    // For other validation errors
                    this.submissionError = Object.values(errors)[0][0]
                    return false
                }

                this.submissionError = 'An unexpected error occurred. Please try again later.'
                return false
            }
        },

        async onSubmit() {
            this.isLoading = true

            const cleanedPhone = this.getCleanedPhoneNumber(this.ssu.phone_number)
            const formattedPhone = this.$options.filters.fixPhone(cleanedPhone, 'E164', true)
            const phoneWithoutCountryCode = formattedPhone.replaceAll(this.ssu.country_code, '')

            const payload = {
                email: this.ssu.email,
                first_name: this.ssu.first_name,
                last_name: this.ssu.last_name,
                full_name: `${this.ssu.first_name} ${this.ssu.last_name}`,
                company_name: this.ssu.company_name,
                phone_number: phoneWithoutCountryCode,
                country_code: this.ssu.country_code,
                country: this.ssu.country,
                team_size: this.ssu.team_size,
                company_size: this.ssu.company_size,
                company_crm: this.ssu.company_crm,
                teams: this.ssu.teams,
                terms_and_conditions: true,
                communications_opt_in: this.ssu.communications_opt_in,
                recaptcha: this.ssu.recaptcha_response || 'test-recaptcha-token',
                hubspotutk: this.getHubspotCookie(),
                referrer: this.ssu.referrer,
                submission_type: PRE_SIGNUP_SUBMISSION_TYPES.ssu.id,
            }

            try {
                await this.submitToRiskAssessment(payload)
                // Proceed with Hubspot if success or validation error
                await this.submitToHubspot()
            } catch (error) {
                console.error('Failed to submit', error)
            } finally {
                this.isLoading = false
                this.isSubmitted = true
            }
        },

        async getUserIpAddress() {
            try {
                const response = await axios.get('/api/v1/risk-assessment/get-ip-address')

                return response?.data?.ip
            } catch (error) {
                console.error('Failed to get user IP address', error)
                return ''
            }
        },

        captureReferrer() {
            const referrerVariants = ['referrer', 'referer', 'referral', 'referal']

            // Loop through each variant to see if it exists in the query parameters
            for (const variant of referrerVariants) {
                const found = this.$route.query[variant] || this.$route.query[variant.toLowerCase()]
                if (found) {
                    this.ssu.referrer = found
                    break // Exit the loop once a match is found
                }
            }
        },

        captureEmail() {
            const emailVariants = ['email', 'email_address']

            // Loop through each variant to see if it exists in the query parameters
            for (const variant of emailVariants) {
                const found = this.$route.query[variant] || this.$route.query[variant.toLowerCase()]
                if (found) {
                    this.ssu.email = found
                    break // Exit the loop once a match is found
                }
            }
        },

        captureTrackingParams() {
            // HubSpot attribution fallback — when hubspotutk is missing or the
            // cross-domain linker didn't decorate the CTA, these query params
            // let HubSpot still resolve the traffic source on contact create.
            const keys = [
                'utm_source', 'utm_medium', 'utm_campaign',
                'utm_content', 'utm_term',
                'gclid', 'gbraid', 'wbraid',
                'fbclid', 'msclkid', '_gcl_aw', '_gcl_au',
                'hsa_cam', 'hsa_grp', 'hsa_ad', 'hsa_src',
                'hsa_acc', 'hsa_net', 'hsa_kw', 'hsa_mt',
                'hsa_tgt', 'hsa_ver',
            ]

            const captured = {}
            keys.forEach(key => {
                const found = this.$route.query[key] || this.$route.query[key.toLowerCase()]
                if (found) {
                    captured[key] = found
                }
            })
            this.trackingParams = captured
        },

        handleTeamsChange(selectedTeams) {
            if (selectedTeams.includes('Select All')) {
                this.ssu.teams = TEAMS_OPTIONS.filter(team => team !== 'Select All')
            } else {
                this.ssu.teams = selectedTeams
            }
        },

        getCurrentStep1Data() {
            return {
                email: this.ssu.email,
                first_name: this.ssu.first_name,
                last_name: this.ssu.last_name,
                company_name: this.ssu.company_name,
                phone_number: this.ssu.phone_number,
                country_code: this.ssu.country_code,
                country: this.ssu.country,
            }
        },

        hasStep1DataChanged() {
            if (!this.lastSubmittedStep1Data) {
                return true
            }

            const currentData = this.getCurrentStep1Data()

            return (
                currentData.email !== this.lastSubmittedStep1Data.email ||
                currentData.first_name !== this.lastSubmittedStep1Data.first_name ||
                currentData.last_name !== this.lastSubmittedStep1Data.last_name ||
                currentData.company_name !== this.lastSubmittedStep1Data.company_name ||
                currentData.phone_number !== this.lastSubmittedStep1Data.phone_number ||
                currentData.country_code !== this.lastSubmittedStep1Data.country_code ||
                currentData.country !== this.lastSubmittedStep1Data.country
            )
        },

        async goToStep2() {
            this.isLoading = true

            try {
                // Check if step 1 data has changed
                if (this.hasStep1DataChanged()) {
                    // Send step 1 data to HubSpot form
                    await this.submitToHubspotStep1()
                    // Store the submitted data
                    this.lastSubmittedStep1Data = this.getCurrentStep1Data()
                }

                this.currentStep = 2
            } catch (error) {
                console.error('Failed to proceed to step 2', error)
                this.submissionError = 'An unexpected error occurred. Please try again later.'
            } finally {
                this.isLoading = false
            }
        },

        goToStep1() {
            this.currentStep = 1
            this.submissionError = null
        }
    },

    watch: {
        'ssu.email': function () {
            this.isEmailValidated = false
        }
    },

    mounted() {
        this.getUserIpAddress().then(ipAddress => {
            this.userIpAddress = ipAddress
        })
        this.captureReferrer()
        this.captureEmail()
        this.captureTrackingParams()
    }
}
</script>

<style scoped>
.signup-progress {
    margin: 20px 0;
}

.signup-progress__bar {
    width: 100%;
    height: 8px;
    background-color: #e9ecef;
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 8px;
}

.signup-progress__fill {
    height: 100%;
    background-color: #007bff;
    border-radius: 4px;
    transition: width 0.3s ease;
}

.signup-progress__text {
    font-size: 14px;
    color: #6c757d;
    font-weight: 500;
}

.signup-header {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    width: 100%;
}

.signup-header .login-brand {
    text-align: center;
    width: 100%;
}

.signup-back-button {
    position: absolute;
    left: 0;
    z-index: 10;
    color: #6c757d !important;
    font-size: 20px;
    padding: 0;
    border: none;
    background: none;
}

.terms-text {
    text-align: center;
    margin-bottom: 15px;
}

.terms-text__content {
    font-size: 11px;
    color: #6c757d;
    margin: 0;
    line-height: 1.4;
    text-align: center;
}

.terms-text__link {
    color: #007bff;
    text-decoration: none;
}

.terms-text__link:hover {
    text-decoration: underline;
}
</style>
